import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { LoggerService, PendoService, Session } from '@enliven/angular';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { VisitorPayload } from '../models/visitor-payload.model';
import { CognitoService } from '../services/cognito.service';
import { DirectLoginService } from '../services/direct-login.service';
import { E360SessionService } from '../services/e360-session.service';
import { E360UserService } from '../services/e360-user.service';
import { DataService } from '../services/data.service';
import { StateService } from '../services/state.service';

export const createUserSessionResolver: ResolveFn<Session | null> = (): Observable<Session | null> => {
  const cognitoService = inject(CognitoService);
  const dataService = inject(DataService);
  const directLoginService = inject(DirectLoginService);
  const e360UserService = inject(E360UserService);
  const e360SessionService = inject(E360SessionService);
  const pendoService = inject(PendoService);
  const stateService = inject(StateService);
  const logger = inject(LoggerService);

  const ssoSignOut = (message: string) => {
    cognitoService.signOut({ message });
  };

  const signOut = (uiMsg: string, logMsg: string, logMsgContext: object) => {
    logger.error(logMsg, logMsgContext);
    if (e360SessionService.isSSO) {
      ssoSignOut(uiMsg);
    } else {
      directLoginService.signOut(uiMsg);
    }
  };

  const resolveVisitorForSession = (): Observable<null> => {
    if (e360SessionService.visitor) {
      return of(null);
    }

    const { userName, adminEmail, firstName, lastName, organizationId } = e360UserService.user;
    const visitorPayload: VisitorPayload = {
      appName: 'e360',
      username: userName,
      email: adminEmail,
      firstName,
      lastName
    };

    return dataService.getVisitor(organizationId, visitorPayload).pipe(
      map(visitor => {
        e360SessionService.visitor = visitor;
        return null;
      }),
      catchError(visitorErr => {
        logger.warn('Error resolving visitor for active session', { visitorErr, organizationId });
        return of(null);
      })
    );
  };

  const patchUserSession = (): Observable<null> => {
    return dataService.patchUser(e360UserService.user).pipe(
      map(() => null),
      catchError(patchUserErr => {
        logger.warn('Error patching user with new session id', { patchUserErr });
        return of(null);
      })
    );
  };

  return e360SessionService.createE360Session().pipe(
    // new session created update user saved session id to handle browser refresh
    switchMap(session => {
      e360UserService.user.savedSessionId = session.sessionId;
      return forkJoin([patchUserSession(), resolveVisitorForSession()]).pipe(
        tap(() => {
          const { visitor, organizationIdpExternalId } = e360SessionService.session;
          const user = e360UserService.user;

          pendoService.initialize({
            visitor: {
              id: visitor?.visitorId ?? '',
              email: user.adminEmail,
              firstName: user.firstName,
              lastName: user.lastName,
              enrolled: visitor?.enrolled ?? false
            },
            account: {
              id: organizationIdpExternalId ?? '',
              accountName: stateService.selectedOrg?.name ?? ''
            }
          });
        }),
        map(() => session)
      );
    }),
    catchError(err => {
      signOut(
        'ALERT_PAGE.PROBLEM_GATHERING_DATA',
        `Error while creating the session for authenticated user id: ${e360UserService.user.id}`,
        { err }
      );
      return of(null);
    })
  );
};
